import { useTranslation } from "react-i18next";
import {
  DataGrid,
  getGridStringOperators,
  GridColumns,
  GridRowsProp,
} from "@mui/x-data-grid";
import { AssociateRow } from "../../../associates/table/AssociatesTable";
import React, { FC, useMemo, useState } from "react";
import { AssociateStatisticsEntry } from "../../../../redux/api/StatisticsApi";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Pagination from "../../../pagination/Pagination";
import { tableComponentProps } from "../../../../common/styles";
import { LinearProgress } from "@mui/material";
import { Toolbar } from "../Toolbar";
import { CloseModalIconButton } from "../../../CloseModalIconButton";
import { getTableLocaleText } from "../../../table/getTableLocaleText";

const pageSizeStorageKey = "associatesAccessStatusTablePageSize";
const defaultPageSize = 10;

interface AssociatesAccessStatusModalProps {
  open: boolean;
  onClose: () => void;
  modalType: string;
  associates: AssociateStatisticsEntry[] | undefined;
}

const AssociatesAccessStatusModal: FC<AssociatesAccessStatusModalProps> = ({
  open,
  onClose,
  modalType,
  associates,
}) => {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState(() => {
    const preferredPageSize = Number(localStorage.getItem(pageSizeStorageKey));
    return isNaN(defaultPageSize) || preferredPageSize === 0
      ? defaultPageSize
      : preferredPageSize;
  });

  const mapDataToTable = (
    associatesStatisticsEntries: AssociateStatisticsEntry[]
  ): GridRowsProp<AssociateRow> => {
    return associatesStatisticsEntries.map(({ publicId, ...rest }) => ({
      ...rest,
      id: publicId,
    }));
  };

  const columns: GridColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("name"),
        flex: 1,
        minWidth: 100,
        filterOperators: getGridStringOperators().filter(
          (op) => op.value === "contains"
        ),
      },
      {
        field: "emailAddress",
        headerName: t("associate.email"),
        flex: 1,
        minWidth: 100,
        filterOperators: getGridStringOperators().filter(
          (op) => op.value === "contains"
        ),
      },
    ],
    [t]
  );

  const rows = useMemo(
    () => (associates ? mapDataToTable(associates) : []),
    [associates]
  );

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    localStorage.setItem(pageSizeStorageKey, `${size}`);
  };

  const localeText = getTableLocaleText();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {`${t("dashboard.associates.title")} / ${t(
          "dashboard.associates.accessStatus." + modalType.toLowerCase()
        )}`}
        <CloseModalIconButton onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <DataGrid
          localeText={localeText}
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          rows={rows}
          rowCount={associates?.length || 0}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          components={{
            Toolbar,
            LoadingOverlay: LinearProgress,
            Pagination,
          }}
          disableSelectionOnClick
          autoHeight
          componentsProps={tableComponentProps}
          disableColumnFilter
        />
      </DialogContent>
    </Dialog>
  );
};

export default AssociatesAccessStatusModal;
